import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IoLogInOutline, IoLogOutOutline, IoPersonOutline, IoTimeOutline, IoRibbonOutline,
  IoPersonAddOutline, IoCreateOutline, IoPersonRemoveOutline, IoFlashOutline,
  IoBanOutline, IoCheckmarkCircleOutline, IoTrashOutline, IoAddCircleOutline,
  IoSwapHorizontalOutline, IoBookmarkOutline, IoChatboxOutline, IoBusinessOutline,
  IoCalendarOutline, IoPeopleOutline, IoCardOutline, IoReceiptOutline, IoHomeOutline,
  IoCheckboxOutline, IoNotificationsOutline, IoEllipsisHorizontal, IoPerson,
} from 'react-icons/io5';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import adminStaffService from '../../../services/adminStaffService';
import arrowleftt from '../../assets/img/arrowleftt.svg';

// ─── Action config (matches mobile exactly) ───────────────────────────────────
const CRM_ACTION_CONFIG = {
  crm_session_started:            { label: 'Started CRM Session',        Icon: IoLogInOutline,            color: '#7C3AED' },
  crm_session_ended:              { label: 'Ended CRM Session',          Icon: IoLogOutOutline,           color: '#6B7280' },
  crm_profile_updated:            { label: 'Updated Profile',            Icon: IoPersonOutline,           color: '#2563EB' },
  crm_subscription_extended:      { label: 'Extended Subscription',      Icon: IoTimeOutline,             color: '#059669' },
  crm_plan_assigned:              { label: 'Assigned Plan',              Icon: IoRibbonOutline,           color: '#059669' },
  crm_sub_user_created:           { label: 'Created Sub-User',           Icon: IoPersonAddOutline,        color: '#D97706' },
  crm_sub_user_updated:           { label: 'Updated Sub-User',           Icon: IoCreateOutline,           color: '#0891B2' },
  crm_sub_user_deleted:           { label: 'Deleted Sub-User',           Icon: IoPersonRemoveOutline,     color: '#DC2626' },
  crm_user_registered:            { label: 'Registered User',            Icon: IoPersonAddOutline,        color: '#059669' },
  crm_credits_assigned:           { label: 'Assigned Credits',           Icon: IoFlashOutline,            color: '#7C3AED' },
  crm_user_disabled:              { label: 'Disabled Account',           Icon: IoBanOutline,              color: '#DC2626' },
  crm_user_enabled:               { label: 'Enabled Account',            Icon: IoCheckmarkCircleOutline,  color: '#059669' },
  crm_user_deleted:               { label: 'Deleted User',               Icon: IoTrashOutline,            color: '#DC2626' },
  crm_lead_created:               { label: 'Created Lead',               Icon: IoAddCircleOutline,        color: '#059669' },
  crm_lead_updated:               { label: 'Updated Lead',               Icon: IoCreateOutline,           color: '#2563EB' },
  crm_lead_deleted:               { label: 'Deleted Lead',               Icon: IoTrashOutline,            color: '#DC2626' },
  crm_lead_status_changed:        { label: 'Changed Lead Status',        Icon: IoSwapHorizontalOutline,   color: '#F59E0B' },
  crm_lead_marked_as_changed:     { label: 'Changed Lead Marked-As',     Icon: IoBookmarkOutline,         color: '#F59E0B' },
  crm_lead_note_added:            { label: 'Added Lead Note',            Icon: IoChatboxOutline,          color: '#6366F1' },
  crm_lead_registered_to_builder: { label: 'Registered Lead to Builder', Icon: IoBusinessOutline,         color: '#7C3AED' },
  crm_site_visit_scheduled:       { label: 'Scheduled Site Visit',       Icon: IoCalendarOutline,         color: '#0891B2' },
  crm_contact_created:            { label: 'Created Contact',            Icon: IoPersonAddOutline,        color: '#059669' },
  crm_contact_updated:            { label: 'Updated Contact',            Icon: IoCreateOutline,           color: '#2563EB' },
  crm_contact_deleted:            { label: 'Deleted Contact',            Icon: IoTrashOutline,            color: '#DC2626' },
  crm_partnership_action:         { label: 'Partnership Action',         Icon: IoPeopleOutline,           color: '#6366F1' },
  crm_subscription_purchased:     { label: 'Purchased Subscription',     Icon: IoCardOutline,             color: '#059669' },
  crm_subscription_action:        { label: 'Subscription Action',        Icon: IoCardOutline,             color: '#2563EB' },
  crm_credits_purchased:          { label: 'Purchased Credits',          Icon: IoFlashOutline,            color: '#059669' },
  crm_invoice_action:             { label: 'Invoice Action',             Icon: IoReceiptOutline,          color: '#D97706' },
  crm_property_action:            { label: 'Property Action',            Icon: IoHomeOutline,             color: '#2563EB' },
  crm_task_action:                { label: 'Task Action',                Icon: IoCheckboxOutline,         color: '#059669' },
  crm_notification_action:        { label: 'Notification Action',        Icon: IoNotificationsOutline,    color: '#F59E0B' },
  crm_session_action:             { label: 'CRM Action',                 Icon: IoEllipsisHorizontal,      color: '#6B7280' },
};

const ROLE_COLORS = {
  agent:   '#7C3AED',
  agency:  '#2563EB',
  builder: '#D97706',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const formatFullDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
};

const isSessionAction = (action) =>
  action === 'crm_session_started' || action === 'crm_session_ended';

// ─── Human-readable description (matches mobile buildHumanDescription exactly) ─
const buildHumanDescription = (item) => {
  const details = item.details || {};
  const entity = details.entity_data || {};
  const target = item.target_user || {};

  const entityName = entity.first_name
    ? `${entity.first_name}${entity.last_name ? ' ' + entity.last_name : ''}`
    : entity.full_name || entity.lead_name || entity.project_name || entity.property_name || entity.property_title || entity.title || null;
  const entityPhone = entity.phone || entity.lead_phone || null;

  switch (item.action) {
    case 'crm_lead_created':
      if (entityName) return `Created lead "${entityName}"${entityPhone ? ` (${entityPhone})` : ''}${entity.city ? ` in ${entity.city}` : ''}`;
      return 'Created a new lead';
    case 'crm_lead_updated':
      if (entityName) return `Updated lead "${entityName}"${details.body_keys?.length ? ` - Fields: ${details.body_keys.join(', ')}` : ''}`;
      if (details.body_keys?.length) return `Updated lead fields: ${details.body_keys.join(', ')}`;
      return 'Updated a lead';
    case 'crm_lead_deleted':
      return entityName ? `Deleted lead "${entityName}"` : 'Deleted a lead';
    case 'crm_lead_status_changed':
      if (entity.status) return `Changed lead status to "${entity.status}"`;
      return 'Changed lead status';
    case 'crm_lead_marked_as_changed':
      if (entity.marked_as) return `Marked lead as "${entity.marked_as}"`;
      return 'Changed lead marked-as';
    case 'crm_lead_note_added': {
      const note = entity.note;
      if (note) return `Added note: "${note.length > 80 ? note.substring(0, 80) + '...' : note}"`;
      return 'Added a note to lead';
    }
    case 'crm_lead_registered_to_builder':
      if (entityName) return `Registered lead "${entityName}" to builder`;
      return 'Registered lead to builder';
    case 'crm_site_visit_scheduled': {
      let desc = 'Scheduled site visit';
      if (entity.project_name) desc += ` for "${entity.project_name}"`;
      if (entity.scheduled_date) desc += ` on ${entity.scheduled_date}`;
      return desc;
    }
    case 'crm_contact_created':
      if (entityName) return `Created contact "${entityName}"${entityPhone ? ` (${entityPhone})` : ''}`;
      return 'Created a new contact';
    case 'crm_contact_updated':
      if (entityName) return `Updated contact "${entityName}"`;
      if (details.body_keys?.length) return `Updated contact fields: ${details.body_keys.join(', ')}`;
      return 'Updated a contact';
    case 'crm_contact_deleted':
      return entityName ? `Deleted contact "${entityName}"` : 'Deleted a contact';
    case 'crm_partnership_action':
      return details.label || 'Partnership action performed';
    case 'crm_subscription_purchased':
      if (entity.plan_name) return `Purchased subscription "${entity.plan_name}"${entity.amount ? ` for ₹${entity.amount}` : ''}`;
      return 'Purchased a subscription';
    case 'crm_subscription_action':
      if (entity.plan_name) return `Subscription action on "${entity.plan_name}"`;
      return details.label || 'Subscription action performed';
    case 'crm_credits_purchased':
      if (entity.credits) return `Purchased ${entity.credits} credits`;
      if (entity.amount) return `Purchased credits worth ₹${entity.amount}`;
      return 'Purchased credits';
    case 'crm_credits_assigned':
      if (details.credits) return `Assigned ${details.credits} credits`;
      if (entity.credits) return `Assigned ${entity.credits} credits`;
      return 'Assigned credits';
    case 'crm_invoice_action':
      return details.label || 'Invoice action performed';
    case 'crm_property_action':
      if (entityName) return `Property action on "${entityName}"`;
      return details.label || 'Property action performed';
    case 'crm_profile_updated':
      if (details.fields_updated?.length) return `Updated profile: ${details.fields_updated.join(', ')}`;
      if (details.body_keys?.length) return `Updated profile fields: ${details.body_keys.join(', ')}`;
      return 'Updated user profile';
    case 'crm_subscription_extended':
      if (details.days) return `Extended subscription by ${details.days} days`;
      return 'Extended subscription';
    case 'crm_plan_assigned':
      if (details.plan_name) return `Assigned plan "${details.plan_name}"${details.days ? ` for ${details.days} days` : ''}`;
      return 'Assigned a plan';
    case 'crm_sub_user_created': {
      const subName = details.sub_user_name || (entity.first_name ? `${entity.first_name}${entity.last_name ? ' ' + entity.last_name : ''}` : entity.full_name);
      const subRole = details.sub_user_role || entity.role;
      if (subName) return `Created sub-user "${subName}"${subRole ? ` (${subRole})` : ''}`;
      return 'Created a sub-user';
    }
    case 'crm_sub_user_updated': {
      const updName = details.sub_user_name || (entity.first_name ? `${entity.first_name}${entity.last_name ? ' ' + entity.last_name : ''}` : entity.full_name);
      if (updName) return `Updated sub-user "${updName}"`;
      if (details.fields_updated?.length) return `Updated sub-user: ${details.fields_updated.join(', ')}`;
      return 'Updated a sub-user';
    }
    case 'crm_sub_user_deleted':
      if (details.sub_user_name) return `Deleted sub-user "${details.sub_user_name}"`;
      return 'Deleted a sub-user';
    case 'crm_user_registered':
      if (details.full_name) return `Registered user "${details.full_name}"${details.role ? ` as ${details.role}` : ''}`;
      if (entityName) return `Registered user "${entityName}"${entity.role ? ` as ${entity.role}` : ''}`;
      return 'Registered a new user';
    case 'crm_user_disabled': {
      const name = target.name || details.target_user_name;
      return name ? `Disabled account of "${name}"` : 'Disabled a user account';
    }
    case 'crm_user_enabled': {
      const name = target.name || details.target_user_name;
      return name ? `Enabled account of "${name}"` : 'Enabled a user account';
    }
    case 'crm_user_deleted': {
      const name = target.name || details.target_user_name;
      return name ? `Permanently deleted user "${name}"` : 'Permanently deleted a user';
    }
    case 'crm_session_started': {
      const name = target.name || details.target_user_name;
      return name ? `Started CRM session for "${name}"` : 'Started CRM session';
    }
    case 'crm_session_ended': {
      const name = target.name || details.target_user_name;
      return name ? `Ended CRM session for "${name}"` : 'Ended CRM session';
    }
    case 'crm_task_action':
      if (entity.title || entity.task_name) return `Task action: "${entity.title || entity.task_name}"`;
      return details.label || 'Task action performed';
    case 'crm_notification_action':
      if (entity.title) return `Sent notification: "${entity.title}"`;
      return details.label || 'Notification action performed';
    case 'crm_session_action':
      return details.label || 'CRM action performed';
    default:
      if (details.label) return details.label;
      return CRM_ACTION_CONFIG[item.action]?.label || item.action;
  }
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function AdminCrmStaffActivityLogs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, userRole, userEmail, userPhone } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const loadLogs = useCallback(async (pageNum = 1) => {
    try {
      if (pageNum === 1) setLoading(true); else setLoadingMore(true);
      const response = await adminStaffService.getActivityLogs({
        resourceId: userId,
        category: 'crm',
        page: pageNum,
        limit: 30,
      });
      let logsData = [], totalCount = 0;
      if (response?.success && response?.data) {
        logsData = response.data.logs || [];
        totalCount = response.data.total || 0;
      } else if (response?.data?.data) {
        logsData = response.data.data.logs || [];
        totalCount = response.data.data.total || 0;
      } else if (response?.logs) {
        logsData = response.logs || [];
        totalCount = response.total || 0;
      }
      setLogs(prev => pageNum === 1 ? logsData : [...prev, ...logsData]);
      setTotal(totalCount);
      setPage(pageNum);
    } catch (e) {
      console.error('Error loading CRM staff activity logs:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId]);

  useEffect(() => { if (userId) loadLogs(1); }, [userId, loadLogs]);

  // User banner initials
  const roleColor = ROLE_COLORS[userRole] || '#6B7280';
  const initials = userName
    ? userName.trim().split(' ').length > 1
      ? (userName.trim().split(' ')[0][0] + userName.trim().split(' ').slice(-1)[0][0]).toUpperCase()
      : userName.substring(0, 2).toUpperCase()
    : 'U';

  // ─── Session card ─────────────────────────────────────────────────────────
  const renderSessionItem = (item, index, isLast) => {
    const isStart = item.action === 'crm_session_started';
    const details = item.details || {};
    const target = item.target_user || {};
    const targetName = target.name || details.target_user_name || userName || '';
    const targetRole = target.role || details.target_user_role || userRole || '';
    const sessionTime = isStart ? (details.started_at || item.created_at) : (details.ended_at || item.created_at);
    const dotColor = isStart ? '#7C3AED' : '#6B7280';
    const IconComp = isStart ? IoLogInOutline : IoLogOutOutline;

    return (
      <div key={item.id || index} style={{ display: 'flex', paddingLeft: 16, paddingRight: 16 }}>
        {/* Timeline left */}
        <div style={{ width: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
          <div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: dotColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
            <IconComp size={12} color="#fff" />
          </div>
          {!isLast && <div style={{ width: 2, flex: 1, backgroundColor: '#E5E7EB', marginTop: -2, minHeight: 20 }} />}
        </div>

        {/* Session card */}
        <div style={{
          flex: 1, borderRadius: 14, padding: 14, marginLeft: 10, marginBottom: 10,
          border: `1.5px solid ${isStart ? '#C4B5FD' : '#D1D5DB'}`,
          backgroundColor: isStart ? '#F5F3FF' : '#F9FAFB',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, backgroundColor: dotColor, padding: '4px 10px', borderRadius: 8 }}>
              <IconComp size={12} color="#fff" />
              <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>
                {isStart ? 'SESSION STARTED' : 'SESSION ENDED'}
              </span>
            </div>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>{getTimeAgo(item.created_at)}</span>
          </div>

          {/* Staff row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
            <IoPersonOutline size={13} color="#6B7280" />
            <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>Staff</span>
            <span style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{item.staff_name || 'System'}</span>
          </div>

          {/* Time */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, backgroundColor: '#fff', padding: '8px 10px', borderRadius: 8, marginBottom: 8, border: '1px solid #F3F4F6' }}>
            <IoTimeOutline size={14} color={dotColor} />
            <span style={{ fontSize: 14, fontWeight: 700, color: isStart ? '#7C3AED' : '#4B5563' }}>
              {formatFullDateTime(sessionTime)}
            </span>
          </div>

          {/* Target user */}
          {targetName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <IoBusinessOutline size={13} color="#6B7280" />
              <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>User</span>
              <span style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>
                {targetName}{targetRole ? ` (${targetRole.charAt(0).toUpperCase() + targetRole.slice(1)})` : ''}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // ─── Regular log card ─────────────────────────────────────────────────────
  const renderLogItem = (item, index, isLast) => {
    if (isSessionAction(item.action)) return renderSessionItem(item, index, isLast);

    const config = CRM_ACTION_CONFIG[item.action] || { label: item.action, Icon: IoEllipsisHorizontal, color: '#6B7280' };
    const { Icon, color, label } = config;
    const description = buildHumanDescription(item);

    return (
      <div key={item.id || index} style={{ display: 'flex', paddingLeft: 16, paddingRight: 16 }}>
        {/* Timeline left */}
        <div style={{ width: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
          <div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
            <Icon size={12} color="#fff" />
          </div>
          {!isLast && <div style={{ width: 2, flex: 1, backgroundColor: '#E5E7EB', marginTop: -2, minHeight: 20 }} />}
        </div>

        {/* Log card */}
        <div style={{
          flex: 1, backgroundColor: '#fff', borderRadius: 14, padding: 14,
          marginLeft: 10, marginBottom: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #F3F4F6',
        }}>
          {/* Header: staff + time */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0 }}>
                <IoPerson size={12} color={color} />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', flex: 1 }}>
                {item.staff_name || 'System'}
              </span>
            </div>
            <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8, whiteSpace: 'nowrap' }}>
              {getTimeAgo(item.created_at)}
            </span>
          </div>

          {/* Description */}
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 10 }}>
            {description}
          </div>

          {/* Footer: action badge + date */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: color + '14', padding: '3px 8px', borderRadius: 8 }}>
              <Icon size={11} color={color} />
              <span style={{ fontSize: 11, fontWeight: 600, color }}>{label}</span>
            </div>
            <span style={{ fontSize: 10, color: '#B0B7C3' }}>{formatDateTime(item.created_at)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="d-flex" style={{ background: '#F9FAFB', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div style={{ paddingBottom: 40 }}>

          {/* Header */}
          <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #E5E7EB', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
              <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>CRM Staff Activity</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {total > 0 && (
                <div style={{ backgroundColor: '#8875FF', borderRadius: 12, padding: '3px 10px', minWidth: 28, textAlign: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{total}</span>
                </div>
              )}
              <button onClick={() => loadLogs(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }} title="Refresh">🔄</button>
            </div>
          </div>

          {/* User banner */}
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: roleColor + '1A', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: roleColor }}>{initials}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName || 'Unknown'}</span>
                {userRole && (
                  <span style={{ backgroundColor: roleColor + '1A', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, color: roleColor, flexShrink: 0 }}>
                    {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                  </span>
                )}
              </div>
              {(userPhone || userEmail) && (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                  {userPhone}{userPhone && userEmail ? '  •  ' : ''}{userEmail}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          {loading && logs.length === 0 ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
            </div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 32px' }}>
              <div style={{ width: 88, height: 88, borderRadius: 44, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 40 }}>📋</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>No CRM Activity</div>
              <div style={{ fontSize: 14, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>
                No staff CRM activity found for {userName || 'this user'}.<br />
                Activity will appear here as staff make changes in CRM.
              </div>
            </div>
          ) : (
            <div style={{ paddingTop: 12, paddingBottom: 24 }}>
              {logs.map((item, index) => renderLogItem(item, index, index === logs.length - 1))}

              {logs.length < total && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button
                    className="personal-uspbtn px-4"
                    style={{ border: 'none', borderRadius: 10 }}
                    onClick={() => loadLogs(page + 1)}
                    disabled={loadingMore}
                  >
                    <span className="personal-uspbtntext">{loadingMore ? 'Loading...' : 'Load More'}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
